import React, { useState, useEffect } from 'react';
import { useCampaign } from '../../contexts/CampaignContext';

// ── XP thresholds per character level ────────────────────────────────────────
const XP_THRESHOLDS = {
  1:  [25,   50,   75,   100],
  2:  [50,   100,  150,  200],
  3:  [75,   150,  225,  400],
  4:  [125,  250,  375,  500],
  5:  [250,  500,  750,  1100],
  6:  [300,  600,  900,  1400],
  7:  [350,  750,  1100, 1700],
  8:  [450,  900,  1400, 2100],
  9:  [550,  1100, 1600, 2400],
  10: [600,  1200, 1900, 2800],
  11: [800,  1600, 2400, 3600],
  12: [1000, 2000, 3000, 4500],
  13: [1100, 2200, 3400, 5100],
  14: [1250, 2500, 3800, 5700],
  15: [1400, 2800, 4300, 6400],
  16: [1600, 3200, 4800, 7200],
  17: [2000, 3900, 5900, 8800],
  18: [2100, 4200, 6300, 9500],
  19: [2400, 4900, 7300, 10900],
  20: [2800, 5700, 8500, 12700],
};

const CR_XP = {
  '0': 10, '0.125': 25, '0.25': 50, '0.5': 100,
  '1': 200, '2': 450, '3': 700, '4': 1100, '5': 1800,
  '6': 2300, '7': 2900, '8': 3900, '9': 5000, '10': 5900,
  '11': 7200, '12': 8400, '13': 10000, '14': 11500, '15': 13000,
  '16': 15000, '17': 18000, '18': 20000, '19': 22000, '20': 25000,
  '21': 33000, '22': 41000, '23': 50000, '24': 62000, '25': 75000,
  '26': 90000, '27': 105000, '28': 120000, '29': 135000, '30': 155000,
};

function getMultiplier(count) {
  if (count === 1)  return 1;
  if (count === 2)  return 1.5;
  if (count <= 6)   return 2;
  if (count <= 10)  return 2.5;
  if (count <= 14)  return 3;
  return 4;
}

function getDifficulty(adjustedXp, thresholds) {
  if (adjustedXp < thresholds[0]) return { label: 'Trivial', color: '#5cb870' };
  if (adjustedXp < thresholds[1]) return { label: 'Easy',    color: '#86efac' };
  if (adjustedXp < thresholds[2]) return { label: 'Medium',  color: '#d4943a' };
  if (adjustedXp < thresholds[3]) return { label: 'Hard',    color: '#e07070' };
  return                                  { label: 'Deadly',  color: '#c0392b' };
}

function fmt(n) {
  return n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n);
}

export default function EncounterCalc({ onHoverToken, encSelecting, setEncSelecting, encSelectedIds, setEncSelectedIds, onActiveMonsterIds }) {
  const { tokens, initiative } = useCampaign();
  const [excludedIds, setExcludedIds] = useState(() => new Set());

  const inCombat = initiative.phase === 'active' && initiative.turnOrder?.length > 0;

  const playerTokens  = Object.values(tokens).filter((t) => t.type === 'player' && !t.defeated);

  // In combat: use tokens in turn order. Out of combat: use all monsters/friendly NPCs on map.
  // Also include any monsters selected from map via the pick tool.
  const autoMonsters = inCombat
    ? initiative.turnOrder
        .map((id) => tokens[id])
        .filter((t) => t && t.type !== 'player' && t.type !== 'object' && !t.defeated)
    : Object.values(tokens).filter((t) =>
        (t.type === 'monster' || t.type === 'friendlyNPC') && !t.defeated
      );

  // Merge auto-detected + manually picked (avoid duplicates)
  const autoIds = new Set(autoMonsters.map((t) => t.id));
  const pickedMonsters = encSelectedIds
    ? [...encSelectedIds]
        .filter((id) => !autoIds.has(id) && tokens[id] && tokens[id].type !== 'player' && tokens[id].type !== 'object')
        .map((id) => tokens[id])
    : [];
  const allCandidates = [...autoMonsters, ...pickedMonsters];

  const monsterTokens = allCandidates.filter((t) => !excludedIds.has(t.id));

  const toggleExcluded = (id) => {
    setExcludedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const includeAll = () => setExcludedIds(new Set());
  const excludeAll = () => setExcludedIds(new Set(allCandidates.map((t) => t.id)));

  const removePicked = (id) => {
    if (setEncSelectedIds) {
      setEncSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  // When monsters are picked from map, auto-include them (remove from excluded)
  useEffect(() => {
    if (!encSelectedIds || encSelectedIds.size === 0) return;
    setExcludedIds((prev) => {
      let changed = false;
      const next = new Set(prev);
      for (const id of encSelectedIds) {
        if (next.has(id)) { next.delete(id); changed = true; }
      }
      return changed ? next : prev;
    });
  }, [encSelectedIds]);

  // Report active (checked) monster IDs to parent for map highlighting
  const activeIdKey = monsterTokens.map((t) => t.id).sort().join(',');
  useEffect(() => {
    if (onActiveMonsterIds) {
      onActiveMonsterIds(new Set(monsterTokens.map((t) => t.id)));
    }
  }, [activeIdKey, onActiveMonsterIds]);

  // Party thresholds
  const party = playerTokens.map((t) => ({
    name: t.name,
    level: Math.min(20, Math.max(1, t.level || 1)),
  }));
  const partyThresholds = [0, 1, 2, 3].map((i) =>
    party.reduce((sum, p) => sum + (XP_THRESHOLDS[p.level]?.[i] || 0), 0)
  );

  const monsterXpTotal  = monsterTokens.reduce((sum, t) => sum + (CR_XP[String(t.cr ?? '')] || 0), 0);
  const multiplier      = getMultiplier(monsterTokens.length);
  const adjustedXp      = Math.round(monsterXpTotal * multiplier);
  const difficulty       = getDifficulty(adjustedXp, partyThresholds);
  const hasResult       = party.length > 0 && monsterTokens.length > 0;

  return (
    <div style={styles.wrapper}>
      <div style={styles.header}>⚖ Encounter Calculator</div>

      <div style={styles.body}>
        {/* Select from map button */}
        {setEncSelecting && (
          <button
            style={{ ...styles.selectBtn, ...(encSelecting ? styles.selectBtnActive : {}) }}
            onClick={() => setEncSelecting((v) => !v)}
          >
            {encSelecting ? '✓ Click monsters on map — done' : '🎯 Select monsters from map'}
          </button>
        )}

        {encSelectedIds && encSelectedIds.size > 0 && (
          <div style={styles.pickedNote}>
            {encSelectedIds.size} picked from map
            <button style={styles.clearPickedBtn} onClick={() => setEncSelectedIds(new Set())}>Clear</button>
          </div>
        )}

        {/* Party summary */}
        <div style={styles.row}>
          <span style={styles.label}>Party</span>
          <span style={styles.value}>
            {party.length} PCs (Lv {party.map((p) => p.level).join(', ')})
          </span>
        </div>

        {/* Thresholds */}
        <div style={styles.thresholds}>
          {['Easy', 'Med', 'Hard', 'Deadly'].map((label, i) => (
            <div key={label} style={styles.threshold}>
              <span style={styles.thresholdLabel}>{label}</span>
              <span style={styles.thresholdVal}>{fmt(partyThresholds[i])}</span>
            </div>
          ))}
        </div>

        {/* Monsters */}
        <div style={styles.row}>
          <span style={styles.label}>Monsters</span>
          <span style={styles.value}>
            {monsterTokens.length}/{allCandidates.length} included
            {allCandidates.length > 0 && (
              <>
                <button style={styles.bulkBtn} onClick={includeAll}>All</button>
                <button style={styles.bulkBtn} onClick={excludeAll}>None</button>
              </>
            )}
          </span>
        </div>

        {allCandidates.length > 0 && (
          <div style={styles.monsterList}>
            {allCandidates.map((t) => {
              const excluded = excludedIds.has(t.id);
              const isPicked = encSelectedIds && encSelectedIds.has(t.id);
              return (
                <div
                  key={t.id}
                  style={{ ...styles.monsterRow, opacity: excluded ? 0.4 : 1 }}
                  onClick={() => toggleExcluded(t.id)}
                  onMouseEnter={onHoverToken ? () => onHoverToken(t.id) : undefined}
                  onMouseLeave={onHoverToken ? () => onHoverToken(null) : undefined}
                >
                  <span style={styles.checkbox}>{excluded ? '☐' : '☑'}</span>
                  <span style={{ ...styles.dot, background: t.colour || '#888' }} />
                  <span style={styles.monsterName}>{t.name}</span>
                  {isPicked && <span style={styles.pickedBadge}>map</span>}
                  <span style={styles.monsterCr}>CR {t.cr ?? '?'}</span>
                  <span style={styles.monsterXp}>{fmt(CR_XP[String(t.cr ?? '')] || 0)}</span>
                  {isPicked && (
                    <button
                      style={styles.removePickedBtn}
                      onClick={(e) => { e.stopPropagation(); removePicked(t.id); }}
                      title="Remove from encounter"
                    >×</button>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* XP calculation */}
        {monsterTokens.length > 0 && (
          <div style={styles.xpSection}>
            <div style={styles.xpRow}>
              <span>Base XP</span>
              <span>{fmt(monsterXpTotal)}</span>
            </div>
            <div style={styles.xpRow}>
              <span>× {multiplier} multiplier</span>
              <span style={{ fontWeight: 700 }}>{fmt(adjustedXp)} XP</span>
            </div>
          </div>
        )}

        {/* Result bar */}
        {hasResult && (
          <div style={{ ...styles.result, borderColor: difficulty.color }}>
            <div style={styles.resultTop}>
              <span style={{ ...styles.diffLabel, color: difficulty.color }}>{difficulty.label}</span>
              <span style={styles.diffXp}>{fmt(adjustedXp)} / {fmt(partyThresholds[3])}</span>
            </div>
            <div style={styles.barOuter}>
              <div style={{ ...styles.barFill, width: `${Math.min(100, (adjustedXp / partyThresholds[3]) * 100)}%`, background: difficulty.color }} />
              {[1, 2, 3].map((i) => (
                <div key={i} style={{ ...styles.marker, left: `${Math.min(99, (partyThresholds[i] / partyThresholds[3]) * 100)}%` }} />
              ))}
            </div>
          </div>
        )}

        {!hasResult && (
          <div style={styles.hint}>
            {party.length === 0 ? 'No player tokens on map.' : 'No monsters to calculate.'}
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  wrapper:         { padding: '0' },
  header:          { padding: '12px 14px', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#d4a847', fontFamily: "'Press Start 2P', monospace", borderBottom: '1px solid #4a3d28' },
  body:            { padding: '10px 14px 14px', display: 'flex', flexDirection: 'column', gap: 8 },
  hint:            { fontSize: 11, color: '#6a5d45', textAlign: 'center', padding: '4px 0' },

  selectBtn:       { width: '100%', padding: '8px 0', background: '#1a2a48', border: '1px solid #4a3d28', borderRadius: 5, color: '#9a8b6e', fontSize: 12, fontWeight: 700, cursor: 'pointer', transition: 'all 0.15s' },
  selectBtnActive: { borderColor: '#d4a847', color: '#d4a847', background: 'rgba(212,168,71,0.1)' },
  pickedNote:      { display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 11, color: '#d4a847', padding: '4px 8px', background: 'rgba(212,168,71,0.08)', borderRadius: 4, border: '1px solid rgba(212,168,71,0.2)' },
  clearPickedBtn:  { background: 'transparent', border: '1px solid #4a3d28', color: '#9a8b6e', fontSize: 10, padding: '1px 6px', borderRadius: 3, cursor: 'pointer' },

  row:             { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  label:           { fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#6a5d45' },
  value:           { fontSize: 12, color: '#9a8b6e' },

  thresholds:      { display: 'flex', gap: 4 },
  threshold:       { flex: 1, background: '#0e1528', borderRadius: 4, padding: '4px 0', textAlign: 'center' },
  thresholdLabel:  { display: 'block', fontSize: 9, color: '#6a5d45', textTransform: 'uppercase', fontWeight: 700 },
  thresholdVal:    { display: 'block', fontSize: 12, fontWeight: 700, color: '#9a8b6e' },

  monsterList:     { display: 'flex', flexDirection: 'column', gap: 2, maxHeight: 240, overflowY: 'auto' },
  monsterRow:      { display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, padding: '3px 2px', color: '#f0e6d0', cursor: 'pointer', borderRadius: 3, transition: 'opacity 0.15s' },
  checkbox:        { fontSize: 13, color: '#d4a847', flexShrink: 0, width: 14 },
  bulkBtn:         { background: 'transparent', border: '1px solid #4a3d28', color: '#9a8b6e', fontSize: 10, padding: '1px 6px', borderRadius: 3, cursor: 'pointer', marginLeft: 4 },
  dot:             { width: 8, height: 8, borderRadius: '50%', flexShrink: 0 },
  monsterName:     { flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  monsterCr:       { color: '#6a5d45', fontSize: 10, flexShrink: 0 },
  monsterXp:       { color: '#6a5d45', fontSize: 10, flexShrink: 0, width: 36, textAlign: 'right' },
  pickedBadge:     { fontSize: 9, fontWeight: 700, color: '#d4a847', background: 'rgba(212,168,71,0.15)', padding: '0 4px', borderRadius: 3, flexShrink: 0 },
  removePickedBtn: { background: 'transparent', border: 'none', color: '#e07070', fontSize: 14, lineHeight: 1, padding: '0 2px', cursor: 'pointer', flexShrink: 0 },

  xpSection:       { background: '#0e1528', borderRadius: 4, padding: '6px 10px', display: 'flex', flexDirection: 'column', gap: 2, fontSize: 11, color: '#9a8b6e' },
  xpRow:           { display: 'flex', justifyContent: 'space-between' },

  result:          { background: '#0e1528', border: '1px solid', borderRadius: 4, padding: '8px 10px' },
  resultTop:       { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 },
  diffLabel:       { fontSize: 16, fontWeight: 900, letterSpacing: '0.04em' },
  diffXp:          { fontSize: 11, color: '#6a5d45' },
  barOuter:        { height: 6, background: '#1a2a48', borderRadius: 3, overflow: 'visible', position: 'relative' },
  barFill:         { height: '100%', borderRadius: 3, transition: 'width 0.4s, background 0.4s' },
  marker:          { position: 'absolute', top: -1, width: 2, height: 8, background: '#4a3d28', borderRadius: 1 },
};
